import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Headeer from "../../components/Headeer";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// FAQ entries structure - will be populated from translations
const getFaqEntries = (t) => [
  {
    category: t('categories.workspaceFiles'),
    question: t('entries.workspaceFiles1.question'),
    answer: t('entries.workspaceFiles1.answer'),
  },
  {
    category: t('categories.patients'),
    question: t('entries.patients1.question'),
    answer: t('entries.patients1.answer'),
  },
  {
    category: t('categories.dynamicForms'),
    question: t('entries.dynamicForms1.question'),
    answer: t('entries.dynamicForms1.answer'),
  },
  {
    category: t('categories.formAnswers'),
    question: t('entries.formAnswers1.question'),
    answer: t('entries.formAnswers1.answer'),
  },
  {
    category: t('categories.automation'),
    question: t('entries.automation1.question'),
    answer: t('entries.automation1.answer'),
  },
  {
    category: t('categories.security'),
    question: t('entries.security1.question'),
    answer: t('entries.security1.answer'),
  },
  {
    category: t('categories.mrSegmentation'),
    question: t('entries.mrSegmentation1.question'),
    answer: t('entries.mrSegmentation1.answer'),
  },
  {
    category: t('categories.scoringFormulas'),
    question: t('entries.scoringFormulas1.question'),
    answer: t('entries.scoringFormulas1.answer'),
  },
  {
    category: t('categories.scoringFormulas'),
    question: t('entries.scoringFormulas2.question'),
    answer: t('entries.scoringFormulas2.answer'),
  },
  {
    category: t('categories.scoringFormulas'),
    question: t('entries.scoringFormulas3.question'),
    answer: t('entries.scoringFormulas3.answer'),
  },
  {
    category: t('categories.scoringFormulas'),
    question: t('entries.scoringFormulas4.question'),
    answer: t('entries.scoringFormulas4.answer'),
  },
  {
    category: t('categories.scoringFormulas'),
    question: t('entries.scoringFormulas5.question'),
    answer: t('entries.scoringFormulas5.answer'),
  },
  {
    category: t('categories.scoringFormulas'),
    question: t('entries.scoringFormulas6.question'),
    answer: t('entries.scoringFormulas6.answer'),
  },
  {
    category: t('categories.scoringFormulas'),
    question: t('entries.scoringFormulas7.question'),
    answer: t('entries.scoringFormulas7.answer'),
  },
  {
    category: t('categories.scoringFormulas'),
    question: t('entries.scoringFormulas8.question'),
    answer: t('entries.scoringFormulas8.answer'),
  },
  {
    category: t('categories.permissionsAccessControl'),
    question: t('entries.permissionsAccessControl1.question'),
    answer: t('entries.permissionsAccessControl1.answer'),
  },
  {
    category: t('categories.permissionsAccessControl'),
    question: t('entries.permissionsAccessControl2.question'),
    answer: t('entries.permissionsAccessControl2.answer'),
  },
  {
    category: t('categories.permissionsAccessControl'),
    question: t('entries.permissionsAccessControl3.question'),
    answer: t('entries.permissionsAccessControl3.answer'),
  },
  {
    category: t('categories.permissionsAccessControl'),
    question: t('entries.permissionsAccessControl4.question'),
    answer: t('entries.permissionsAccessControl4.answer'),
  },
  {
    category: t('categories.permissionsAccessControl'),
    question: t('entries.permissionsAccessControl5.question'),
    answer: t('entries.permissionsAccessControl5.answer'),
  },
  {
    category: t('categories.permissionsAccessControl'),
    question: t('entries.permissionsAccessControl6.question'),
    answer: t('entries.permissionsAccessControl6.answer'),
  },
  {
    category: t('categories.permissionsAccessControl'),
    question: t('entries.permissionsAccessControl7.question'),
    answer: t('entries.permissionsAccessControl7.answer'),
  },
  {
    category: t('categories.permissionsAccessControl'),
    question: t('entries.permissionsAccessControl8.question'),
    answer: t('entries.permissionsAccessControl8.answer'),
  },
];

export default function Faq() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('faq');

  const faqEntries = useMemo(() => getFaqEntries(t), [t]);

  const groupedFaqs = useMemo(() => {
    const map = new Map();
    faqEntries.forEach((entry) => {
      if (!map.has(entry.category)) {
        map.set(entry.category, []);
      }
      map.get(entry.category).push(entry);
    });
    return Array.from(map.entries());
  }, [faqEntries]);

  return (
    <Box sx={{ marginLeft: 2, marginRight: 2 }}>
      <Headeer title={t('title')} subtitle={t('subtitle')} />

      <Box
        sx={{
          width: "92%",
          mx: "auto",
          mt: 4,
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: `0 18px 38px ${colors.primary[900]}55`,
          border: `1px solid ${colors.primary[200]}25`,
          backgroundColor: colors.primary[500],
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 3, md: 4 },
            py: 3,
            background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                backgroundColor: colors.primary[500],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: colors.grey[100],
                boxShadow: `0 12px 28px ${colors.primary[900]}55`,
              }}
            >
              <QuestionAnswerOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: colors.grey[100],
                  fontWeight: 700,
                  fontSize: "1.35rem",
                  letterSpacing: 0.5,
                }}
              >
                {t('howCanWeHelp')}
              </Typography>
              <Typography sx={{ color: colors.grey[200], opacity: 0.85 }}>
                {t('findQuickAnswers')}
              </Typography>
            </Box>
          </Stack>

          <Chip
            icon={<HelpOutlineOutlinedIcon />}
            label={t('needMoreContact')}
            sx={{
              backgroundColor: `${colors.primary[500]}aa`,
              color: colors.grey[100],
              fontWeight: 600,
              borderRadius: 2,
              px: 1.8,
              "& .MuiChip-icon": {
                color: colors.greenAccent[400],
              },
            }}
          />
        </Box>

        <Box sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 } }}>
          {groupedFaqs.map(([category, items], idx) => (
            <Box key={category} sx={{ mb: idx === groupedFaqs.length - 1 ? 0 : 4 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: 2.5 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.greenAccent[400],
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                  }}
                >
                  {category}
                </Typography>
                <Divider
                  sx={{
                    flexGrow: 1,
                    borderColor: `${colors.greenAccent[500]}55`,
                  }}
                />
                <Chip
                  label={`${items.length} ${
                    items.length === 1 ? t('question') : t('questions')
                  }`}
                  size="small"
                  sx={{
                    backgroundColor: `${colors.primary[400]}aa`,
                    color: colors.grey[100],
                    borderRadius: 2,
                    fontSize: "0.75rem",
                  }}
                />
              </Stack>

              <Stack spacing={2}>
                {items.map((item, questionIdx) => (
                  <Accordion
                    key={item.question}
                    disableGutters
                    square={false}
                    sx={{
                      backgroundColor: colors.primary[500],
                      borderRadius: 3,
                      border: `1px solid ${colors.primary[200]}30`,
                      boxShadow: `0 12px 28px ${colors.primary[900]}30`,
                      "&:before": { display: "none" },
                      "& .MuiAccordionSummary-root": {
                        px: { xs: 2, md: 3 },
                        py: { xs: 1.5, md: 1.8 },
                        background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
                        borderRadius: "inherit",
                        minHeight: "auto",
                        "& .MuiAccordionSummary-content": {
                          margin: 0,
                        },
                      },
                      "& .MuiAccordionDetails-root": {
                        px: { xs: 2, md: 3 },
                        py: { xs: 2, md: 2.5 },
                        backgroundColor: colors.primary[500],
                      },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.greenAccent[400] }} />}>
                      <Stack spacing={0.5}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: colors.grey[400],
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {`Q${questionIdx + 1}`}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ color: colors.grey[100], fontWeight: 600 }}
                        >
                          {item.question}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        sx={{
                          color: colors.grey[200],
                          lineHeight: 1.65,
                          letterSpacing: 0.2,
                        }}
                      >
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}